import { FunctionComponent, useEffect, useState, useRef } from 'react';
import { isMobile } from 'react-device-detect';
import { Container } from '@material-ui/core';
import { RouteComponentProps } from '@reach/router';
import RouteBuilderContext from 'src/utils/routing/RouteBuilderContext';
import RouteBuilder from 'src/utils/routing/RouteBuilder';
import NavigationPane from 'src/components/NavigationPane/NavigationPane';
import { FunctionComponentProps, FunctionComponentReturnType } from 'src/types/sharedReact';
import { ClientPath } from 'src/utils/routing/routes';
import {
    AuthenticationContextState,
    useAuthenticationContext
} from 'src/contextProviders/AuthenticationProvider/AuthenticationContext';
import Header from 'src/components/Header/Header';
import ErrorBoundary from 'src/containers/ErrorBoundary/ErrorBoundary';
import NotAuthorizedPage from 'src/pages/NotAuthorizedPage/NotAuthorizedPage';
import Footer from 'src/components/Footer/Footer';
import DevExFooter from 'src/components/DevExFooter/DevExFooter';
import BannerUtility from 'src/components/BannerUtility/BannerUtility';
import 'src/pages/PageTemplate/PageTemplate.css';
import 'src/scripts/oneTag.js';
import LogoutPage from 'src/pages/LogoutPage/LogoutPage';
import LoginPage from 'src/pages/LoginPage/LoginPage';
import { getCsrfToken } from 'src/utils/csrfToken';
import HomePage from 'src/pages/HomePage/HomePage';
import { handleAuthentication } from 'src/utils/handleAuthentication';
import { Environment } from 'src/types/environment';
import clsx from 'clsx';
import MobileMenu from 'src/components/NavigationPane/MobileMenu';
import { Feature } from 'src/utils/feature';

import { isUserAuthorizedToSeePage } from 'src/utils/userAuthorizationUtils';
import MaintenanceBanner from 'src/components/MaintenanceBanner/MaintenanceBanner';
import DxoUiHeader, { getHeaderTabs } from 'src/components/Dxo/DxoUiHeader';

export interface PageTemplateProps extends RouteComponentProps, FunctionComponentProps {
    Page: FunctionComponent<any>;
    path?: ClientPath;
    requiredFeatures: Feature[];
}

declare const window: any;

function PageTemplate(
    {
        Page,
        requiredFeatures,
        location,
        path,
        uri
    }: PageTemplateProps
): FunctionComponentReturnType {
    const routeBuilder: RouteBuilder = new RouteBuilder({
        location,
        path,
        uri,
        apiUrlBase: ""
    });
    const {
        isAuthenticated,
        setIsAuthenticated,
        user,
        setUser
    }: AuthenticationContextState = useAuthenticationContext();

    const getIsUserAuthenticatedRoute = routeBuilder.api.getIsUserAuthenticated();

    const isOnDevex: boolean = window.shouldUseCaliperX;
    const isLogoutPage: boolean = Page === LogoutPage;
    const isLoginPage: boolean = Page === LoginPage;
    const isHomePage: boolean = Page === HomePage;
    const [isAuthenticating, setIsAuthenticating] = useState<boolean>(isAuthenticated);
    const bannerUtilityRef = useRef()
    useEffect(
        () => {
            const getUserRoute = routeBuilder.api.getUser();
            const loginRoute = routeBuilder.api.login();
            const partnersAccessRoute = routeBuilder.api.getAllDashboardsPartners();
            if (isLogoutPage || isLoginPage) {
                return;
            }
            if (!isAuthenticating) {
                setIsAuthenticating(true)
                handleAuthentication(routeBuilder, setIsAuthenticated, setUser, () => { setIsAuthenticating(false) });
            }
        },
        [
            isLogoutPage,
            isLoginPage,
            setIsAuthenticated,
            setUser,
            setIsAuthenticating
        ]
    );

    /**
     * Re-fetch the user (and their access list) on every SPA route change so a
     * server-side role/permission change is picked up without requiring a hard
     * refresh. Throttled to at most one re-fetch per AUTH_REFRESH_THROTTLE_MS
     * window to avoid hammering the API on rapid back/forward clicks.
     *
     * Skipped on the login/logout pages (they own their own auth lifecycle) and
     * during the initial mount (the effect above already runs auth there).
     */
    const lastAuthRefreshAtRef = useRef<number>(Date.now());
    const AUTH_REFRESH_THROTTLE_MS = 30 * 1000;
    useEffect(
        () => {
            if (isLogoutPage || isLoginPage) return;
            if (!isAuthenticated || !user) return;
            if (isAuthenticating) return;
            const now = Date.now();
            if (now - lastAuthRefreshAtRef.current < AUTH_REFRESH_THROTTLE_MS) return;
            lastAuthRefreshAtRef.current = now;
            setIsAuthenticating(true);
            handleAuthentication(routeBuilder, setIsAuthenticated, setUser, () => { setIsAuthenticating(false); });
            // routeBuilder is stable across renders; we intentionally key off pathname only.
            // eslint-disable-next-line react-hooks/exhaustive-deps
        },
        [location?.pathname]
    );

    useEffect(() => {
        if (isAuthenticated && user) {
            let email_domain = user.email.split('@')[1] || 'invalid_email';
            console.log('Tracking page view for', email_domain, 'on page', routeBuilder.client.getCurrentPageName());
            window.sp?.('trackPageView', 'COPP:' + routeBuilder.client.getCurrentPageName());
            window.oneTag?.track('view', {
                event_name: 'page_view',
                application_name: 'DevEx Analytics',
                brand_text: email_domain,
            });
        }
    }, [isAuthenticated]);
    let bannerFunction;

    return (isAuthenticated || user || isLogoutPage || isLoginPage) ? (
        <RouteBuilderContext.Provider value={routeBuilder}>
            {isOnDevex ? <div className="dxo-header-wrapper">
                <DxoUiHeader
                    logoImageSrc='/partnerships/analytics/logos/cof_partnerships.png'
                    logoClickHref='/partnerships/analytics/'
                    logoAltText='Capital One Partnerships Logo'
                    headertabs={isAuthenticated ? getHeaderTabs(user) : []}
                    activeHeaderTab='Analytics'
                    mobileMenu={true}
                    hideUserMenuOptions={true}
                    authentication={{
                        authenticated: isAuthenticated,
                        login: () => { },
                        logout: () => {
                            (async () => {
                                try {
                                    // Logout is a state-changing POST protected by CSRF (VULN-013).
                                    // The token is held server-side in the session and published in
                                    // the backend's X-CSRF-TOKEN response header; axios interceptors
                                    // don't cover fetch, so attach it explicitly here.
                                    const xsrfToken = getCsrfToken();
                                    await fetch(routeBuilder.api.logout(), {
                                        method: 'POST',
                                        credentials: 'include',
                                        redirect: 'manual',
                                        headers: xsrfToken ? { 'X-XSRF-TOKEN': xsrfToken } : {},
                                    });
                                } catch (e) {
                                    console.error('COPP logout failed:', e);
                                }
                                try {
                                    await fetch('/oauth2/oidc/logout', { mode: 'no-cors' });
                                    new BroadcastChannel('session').postMessage({ type: 'logout' });
                                } catch (e) {
                                    console.error('CaliperX2 logout failed:', e);
                                }
                                try { window.localStorage?.clear(); } catch (e) { console.error('Failed to clear localStorage on logout:', e); }
                                try { window.sessionStorage?.clear(); } catch (e) { console.error('Failed to clear sessionStorage on logout:', e); }
                                window.location.assign('/auth/sign-in?returnTo=%2Fpartnerships%2Fanalytics%2F');
                            })();
                        },
                        displayName: user?.firstName ? `${user.firstName} ${user.lastName ?? ''}`.trim() : '',
                        default: false,
                    }}
                />
            </div> : <Header />}
            <div>
                {!isOnDevex && (!isMobile ? <NavigationPane /> : <MobileMenu />)}
                <BannerUtility ref={bannerUtilityRef} />
                {isAuthenticated && !isLogoutPage && !isLoginPage && <MaintenanceBanner bannerUtilityRef={bannerUtilityRef} />}
                <div className={clsx("main-wrapper", isOnDevex && "main-wrapper--no-nav")}>
                    <Container component="main" disableGutters={isOnDevex} maxWidth={isOnDevex ? false : undefined}>
                        <ErrorBoundary>
                            {!isOnDevex && <h1 id="page-template-title-element"
                                className={clsx(isMobile ? 'page-template-mobile-title' : 'page-template-desktop-title')}>
                                {routeBuilder.client.getCurrentPageName()}
                            </h1>}
                            {
                                isUserAuthorizedToSeePage(
                                    user,
                                    requiredFeatures,
                                    routeBuilder,
                                    isLogoutPage,
                                    isLoginPage
                                ) ?
                                    <Page
                                        bannerUtilityRef={bannerUtilityRef}
                                    /> :
                                    <NotAuthorizedPage />
                            }
                        </ErrorBoundary>
                    </Container>
                    {isOnDevex ? <DevExFooter /> : <Footer />}
                </div>
            </div>
        </RouteBuilderContext.Provider>
    ) : <div className={"grv-spinner grv-spinner--active"} id="buttonSpinnerLight"></div>;
}

export default PageTemplate;
